import React from "react";
import { STATUS_COLORS } from "@/lib/api";

export function Badge({ level = "inactive", children, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_COLORS[level] || STATUS_COLORS.inactive}`}
    >
      {children}
    </span>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div {...rest} className={`bg-white/80 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200/60 dark:border-slate-700/50 rounded-xl shadow-sm ${className}`}>{children}</div>;
}

export function PageHeader({ title, description, actions }) {
  // iter153 — Mobil telefon (≤640px): başlık ve action butonları alt alta,
  // butonlar wrap ederek ekran dışına taşmaz. sm+ (≥640px) mevcut yatay
  // desktop görünüm aynen korunur.
  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-4 pb-3 sm:pb-4 border-b border-slate-200/50 dark:border-slate-700/50">
      <div className="min-w-0 sm:min-w-[240px] sm:flex-1">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight break-words" style={{ fontFamily: "'Manrope', sans-serif", background: "linear-gradient(90deg,#6366f1 0%,#c084fc 60%,#f472b6 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{title}</h1>
        {description && <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</div>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 min-w-0 sm:justify-end">{actions}</div>}
    </div>
  );
}

export function Btn({ children, variant = "primary", size = "md", className = "", ...props }) {
  const base = "inline-flex items-center gap-1.5 font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes = { sm: "px-2.5 py-1 text-xs", md: "px-3.5 py-1.5 text-sm", lg: "px-4 py-2 text-sm", xl: "px-6 py-3 text-base font-semibold" };
  const variants = {
    primary: "text-white shadow-sm hover:brightness-105 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 dark:from-violet-600 dark:via-purple-600 dark:to-fuchsia-600",
    secondary: "bg-white/70 dark:bg-slate-800/70 backdrop-blur border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-700",
    danger: "text-white shadow-sm hover:brightness-105 bg-gradient-to-r from-rose-500 to-red-500",
    ghost: "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
    soft: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/20",
    approve: "text-white shadow-md hover:brightness-110 hover:shadow-lg bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-2 ring-emerald-200/50 dark:ring-emerald-400/30",
    reject: "text-white shadow-md hover:brightness-110 bg-gradient-to-r from-rose-500 via-red-500 to-orange-500",
    warn: "text-white shadow-md hover:brightness-110 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500",
  };
  return <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...props}>{children}</button>;
}

/**
 * iter108: Reusable zero-on-focus handlers for MANUAL numeric inputs.
 *
 * Semantics:
 *   - Focus: if the current display is exactly '0' / '' , show empty for typing.
 *   - Blur: if the user leaves it empty, restore '0'.
 *   - Non-zero values are NEVER auto-cleared.
 *   - readOnly / disabled inputs are exempt (backend-authoritative values).
 *   - The focus->empty transition is UI-only; it does NOT call onChange, so
 *     autosave / debounced persistence NEVER sees a spurious '' / null state.
 *   - Blur restores '0' via onChange only if the underlying value was truly ''
 *     (i.e. user cleared without typing) — no-op otherwise.
 *
 * Use `manualEntry={false}` to opt out (e.g. read-only computed numeric).
 */
export function useZeroOnFocusHandlers(props) {
  const isNumeric = props.type === "number";
  const enabled = isNumeric && !props.readOnly && !props.disabled && props.manualEntry !== false;
  const onFocus = React.useCallback((e) => {
    if (enabled) {
      const v = e.target.value;
      if (v === "0" || v === 0 || Number(v) === 0) {
        // UI-only mutation; React state (props.value) untouched → no autosave fire.
        e.target.value = "";
      }
    }
    props.onFocus && props.onFocus(e);
  }, [enabled, props]);
  const onBlur = React.useCallback((e) => {
    if (enabled && e.target.value === "") {
      e.target.value = "0";
      // Only propagate if the committed React state is not already 0 — this
      // avoids redundant setState calls during typical focus-then-blur cycles
      // where the underlying value remained 0 the whole time.
      const currentPropValue = props.value;
      const needsSync = currentPropValue !== 0 && currentPropValue !== "0" && currentPropValue !== "";
      if (needsSync && props.onChange) {
        props.onChange(e);
      }
    }
    props.onBlur && props.onBlur(e);
  }, [enabled, props]);
  return { onFocus, onBlur };
}

export function Input({ label, error, manualEntry, ...props }) {
  const { onFocus, onBlur } = useZeroOnFocusHandlers({ ...props, manualEntry });
  return (
    <label className="block">
      {label && <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-1">{label}</span>}
      <input {...props} onFocus={onFocus} onBlur={onBlur} className={`w-full px-2.5 py-1.5 text-sm bg-white dark:bg-slate-800/70 border ${error ? "border-red-400" : "border-slate-200 dark:border-slate-700"} text-slate-800 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`} />
      {error && <span className="text-xs text-red-600 mt-0.5">{error}</span>}
    </label>
  );
}

/**
 * Bare numeric input for table cells / compact rows where the labelled
 * `Input` chrome is undesirable. Applies the same manual-entry UX.
 * Pass `manualEntry={false}` to disable focus-clear behaviour.
 */
export function NumInput({ manualEntry, className = "", ...props }) {
  const { onFocus, onBlur } = useZeroOnFocusHandlers({ ...props, type: "number", manualEntry });
  return <input {...props} type="number" onFocus={onFocus} onBlur={onBlur} className={className} />;
}

export function Select({ label, children, ...props }) {
  return (
    <label className="block">
      {label && <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-1">{label}</span>}
      <select {...props} className="w-full px-2.5 py-1.5 text-sm bg-white dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500">{children}</select>
    </label>
  );
}

export function Textarea({ label, ...props }) {
  return (
    <label className="block">
      {label && <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-1">{label}</span>}
      <textarea {...props} className="w-full px-2.5 py-1.5 text-sm bg-white dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" rows={3} />
    </label>
  );
}

export function Table({ columns, rows, empty = "Kayıt yok", onRowClick, testid }) {
  // iter154 — Table primitive artık aynı columns/rows contract ile:
  //   ≥640px (sm+): mevcut yatay tablo (desktop davranış aynen korunur)
  //   <640px      : dikey stacked card list — ilk anlamlı label kolonu kart
  //                 başlığı, "İşlem" (actions) kolonu kart alt bölümüne düşer.
  // Business logic, columns API, onRowClick semantiği tam korunur.
  // Guard against JSX-node labels: only string labels get lowercased.
  const labelStr = (l) => (typeof l === "string" ? l : "");
  const primaryIdx = columns.findIndex((c) => labelStr(c.label).trim() !== "");
  const actionIdx = columns.findIndex((c) => {
    const s = labelStr(c.label).toLowerCase();
    return s === "i̇şlem" || s === "işlem";
  });
  const detailColumns = columns
    .map((c, i) => ({ c, i }))
    .filter(({ c, i }) => i !== primaryIdx && i !== actionIdx && labelStr(c.label).trim() !== "");

  return (
    <>
      {/* iter255: Desktop / tablet — mevcut tablo. Hücreler tablet dar
          viewport'ta wrap edebilsin diye `whitespace-nowrap` sadece başlıklarda
          korunur; td'ler doğal sarma davranışına döner. Mobile card görünümü
          <640px'de aktifleşir (değişmedi). */}
      <div className="hidden sm:block overflow-x-auto bg-white/80 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200/60 dark:border-slate-700/50 rounded-xl shadow-sm max-w-full">
        <table className="w-full text-sm" data-testid={testid}>
          <thead>
            <tr className="border-b border-slate-200/70 dark:border-slate-700/50 bg-gradient-to-r from-indigo-50/60 via-violet-50/40 to-pink-50/40 dark:from-violet-900/30 dark:via-purple-900/25 dark:to-fuchsia-900/25">
              {columns.map((c, i) => (
                <th key={i} className="text-left px-3 py-2.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center py-10 text-slate-400 dark:text-slate-500 text-sm">{empty}</td></tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={r.id || i}
                className={`border-b border-slate-100/70 dark:border-slate-700/30 last:border-0 ${onRowClick ? "hover:bg-indigo-50/30 dark:hover:bg-violet-500/10 cursor-pointer transition-colors" : ""}`}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
              >
                {columns.map((c, j) => (
                  <td key={j} className="px-3 py-2 text-slate-700 dark:text-slate-200 align-top lg:whitespace-nowrap break-words">{c.render ? c.render(r) : r[c.key]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile — stacked cards */}
      <div className="sm:hidden space-y-2" data-testid={testid ? `${testid}-mobile` : undefined}>
        {rows.length === 0 && (
          <div className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm bg-white/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg">{empty}</div>
        )}
        {rows.map((r, i) => {
          const primary = primaryIdx >= 0 ? (columns[primaryIdx].render ? columns[primaryIdx].render(r) : r[columns[primaryIdx].key]) : null;
          const actions = actionIdx >= 0 ? (columns[actionIdx].render ? columns[actionIdx].render(r) : r[columns[actionIdx].key]) : null;
          return (
            <div
              key={r.id || i}
              onClick={onRowClick ? (e) => { if (e.target.closest("button,a,input,select,textarea")) return; onRowClick(r); } : undefined}
              className={`bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-sm ${onRowClick ? "active:bg-slate-50 dark:active:bg-slate-700/50 cursor-pointer" : ""}`}
              data-testid={r.id ? `mobile-row-${r.id}` : undefined}
            >
              {primary != null && (
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1.5 break-words">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1.5">{columns[primaryIdx]?.label}:</span>{primary}
                </div>
              )}
              {detailColumns.length > 0 && (
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {detailColumns.map(({ c, i: colI }) => (
                    <React.Fragment key={colI}>
                      <div className="text-slate-500 dark:text-slate-400 uppercase tracking-wide text-[10px]">{c.label}</div>
                      <div className="text-slate-800 dark:text-slate-200 text-right break-words">{c.render ? c.render(r) : r[c.key]}</div>
                    </React.Fragment>
                  ))}
                </div>
              )}
              {actions != null && (
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700 flex flex-wrap gap-1.5 justify-end">{actions}</div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;
  const sizes = { md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl", full: "max-w-6xl" };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 dark:bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="modal-backdrop">
      <div className={`bg-white dark:bg-slate-900 rounded-t-xl sm:rounded-xl shadow-2xl w-full ${sizes[size]} max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur">
          <h2 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 pr-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="modal-close" aria-label="Kapat">×</button>
        </div>
        <div className="p-3 sm:p-4 text-slate-800 dark:text-slate-200">{children}</div>
      </div>
    </div>
  );
}
